import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  TableSortLabel,
  Autocomplete,
  TextField,
  Box,
  Typography
} from '@mui/material';
import ErrorDialog from '../ErrorDialog';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { rpc } from '../utils/RpcUtils';
import * as Constants from '../Constants';
import '../App.css';

const SiteAdminList = () => {
  const [admins, setAdmins] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAdmins = async () => {
      const params = {
        roles: Constants.ROLE_COMMUNITY_ADMIN
      };
      rpc({
        method: "GetUsers",
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setAdmins
      });
    };
    fetchAdmins();
  }, []);

  // --- Helper Methods ---

  const createSiteAdmin = () => {
    navigate(`/createSiteAdmin`);
  };

  const handleClose = () => {
    setOpenErrorDialog(false);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // --- Navigation Handler for Autocomplete ---

  const handleAutocompleteSelect = (event, selectedAdmin) => {
    if (selectedAdmin && selectedAdmin._id) {
      navigate(`/siteAdmin/${selectedAdmin._id}`);
    }
  };

  // --- Sorting Logic ---

  const sortedAdmins = admins.slice().sort((a, b) => {
    if (orderBy === 'last') {
      return (order === 'asc' ? (a.last || "").localeCompare(b.last || "") : (b.last || "").localeCompare(a.last || ""));
    } else if (orderBy === 'first') {
      return (order === 'asc' ? (a.first || "").localeCompare(b.first || "") : (b.first || "").localeCompare(a.first || ""));
    } else if (orderBy === 'email') {
      return (order === 'asc' ? (a.email || "").localeCompare(b.email || "") : (b.email || "").localeCompare(a.email || ""));
    } else if (orderBy === 'site') {
      const siteA = a._site?.name || "";
      const siteB = b._site?.name || "";
      return (order === 'asc' ? siteA.localeCompare(siteB) : siteB.localeCompare(siteA));
    }
    return 0;
  });

  return (
    <div className="SiteAdminList" style={{ padding: '20px' }}>
      {/* Search and Actions Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3, 
        gap: 2,
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '8px'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', minWidth: 'fit-content' }}>
            Admin Search:
          </Typography>
          <Autocomplete
            disablePortal
            id="admin-search-nav"
            options={admins}
            getOptionLabel={(option) => 
              `${option.first} ${option.last} (${option.email}) - ${option._site?.name || 'No Site'}`
            }
            sx={{ width: 500, backgroundColor: 'white' }}
            onChange={handleAutocompleteSelect}
            renderInput={(params) => (
              <TextField {...params} label="Search by name, email, or site..." variant="outlined" size="small" />
            )}
          />
        </Box>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={createSiteAdmin}
          sx={{ height: '40px' }}
        >
          Create Admin
        </Button>
      </Box>

      {/* Main Table */}
      <TableContainer className="TableContainer" component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'last'}
                  direction={orderBy === 'last' ? order : 'asc'}
                  onClick={() => handleSort('last')}
                >
                  <b>Last</b>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'first'}
                  direction={orderBy === 'first' ? order : 'asc'}
                  onClick={() => handleSort('first')}
                >
                  <b>First</b>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  <b>Email</b>
                </TableSortLabel>
              </TableCell>
              <TableCell><b>Phone</b></TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'site'}
                  direction={orderBy === 'site' ? order : 'asc'}
                  onClick={() => handleSort('site')}
                >
                  <b>Site</b>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAdmins.map((admin) => (
              <TableRow key={admin._id} hover>
                <TableCell>
                  <Link to={`/siteAdmin/${admin._id}`}>{admin.last}</Link>
                </TableCell>
                <TableCell>{admin.first}</TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>{admin.phone}</TableCell>
                <TableCell>
                  {/* Clickable Site Link */}
                  {admin._site ? (
                    <Link 
                      to={`/site/${admin._site._id}`} 
                      style={{ textDecoration: 'none', color: '#1976d2' }}
                    >
                      {admin._site.name}
                    </Link>
                  ) : (
                    ''
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ErrorDialog 
        open={openErrorDialog} 
        onErrorDialogClose={handleClose} 
        errorMessage={errorMessage} 
      />
    </div>
  );
};

export default SiteAdminList;